import { useMemo } from 'react';
import { normalizeStatus } from '../lib/subscriptionStatus';

/**
 * Determines what actions a user can take based on their subscription status
 * @param {Object} subscription - The subscription object from the API
 * @returns {Object} Subscription state with permissions and messages
 */
export default function useSubscriptionState(subscription) {
  return useMemo(() => {
    if (!subscription) {
      return {
        status: 'CANCELLED',
        canChange: false,
        canSubscribe: true,
        blockReason: undefined,
        actionRequired: null
      };
    }

    // Normalize status to uppercase
    const status = normalizeStatus(subscription.status);

    switch (status) {
      case 'PROCESSING':
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: 'Payment in progress. Please wait.',
          actionRequired: 'wait'
        };

      case 'RETRYING':
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: 'Payment failed. Update your payment method to continue.',
          actionRequired: 'update_payment'
        };

      case 'SUSPENDED':
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: 'Subscription suspended due to payment failures. Please contact support.',
          actionRequired: 'contact_support'
        };

      case 'ACTIVE':
      case 'SUCCESS':
        return {
          status: 'ACTIVE',
          canChange: true,
          canSubscribe: false,
          blockReason: undefined,
          actionRequired: null
        };

      case 'CANCELLED':
      case 'CANCELED':
        // Check if in grace period
        if (subscription.gracePeriod !== null && subscription.gracePeriod >= 0) {
          return {
            status: 'ACTIVE',
            canChange: true,
            canSubscribe: false,
            blockReason: undefined,
            actionRequired: null,
            isInGracePeriod: true
          };
        }
        return {
          status: 'CANCELLED',
          canChange: false,
          canSubscribe: true,
          blockReason: undefined,
          actionRequired: null
        };

      case 'FAILED':
        return {
          status,
          canChange: false,
          canSubscribe: true,
          blockReason: undefined,
          actionRequired: null
        };

      case 'REFUNDED':
        return {
          status,
          canChange: false,
          canSubscribe: true,
          blockReason: undefined,
          actionRequired: null
        };

      case 'PENDING':
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: 'Payment pending.',
          actionRequired: 'complete_payment'
        };

      case 'INITIATED':
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: 'Subscription is being set up.',
          actionRequired: 'wait'
        };

      default:
        return {
          status,
          canChange: false,
          canSubscribe: false,
          blockReason: undefined,
          actionRequired: null
        };
    }
  }, [subscription]);
}
